import React, { Component } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Form } from "react-final-form";

import { branding } from "stripes-config";

import {
  TextField,
  Button,
  Row,
  Col,
  Headline,
} from "@folio/stripes-components";

import SSOLogin from "../SSOLogin";
import OrganizationLogo from "../OrganizationLogo";
import AuthErrorsContainer from "../AuthErrorsContainer";
import { RecekField } from "../inputs";

import styles from "./Login.css";

class Login extends Component {
  static propTypes = {
    ssoActive: PropTypes.bool,
    authErrors: PropTypes.arrayOf(PropTypes.object),
    onSubmit: PropTypes.func.isRequired,
    handleSSOLogin: PropTypes.func.isRequired,
  };

  static defaultProps = {
    authErrors: [],
    ssoActive: false,
  };

  state = {
    showPassword: false, // Track password visibility
  };

  togglePasswordVisibility = () => {
    this.setState((prevState) => ({ showPassword: !prevState.showPassword }));
  };

  render() {
    const { authErrors, handleSSOLogin, ssoActive, onSubmit } = this.props;

    const { showPassword } = this.state;

    const version = "1.6.0-SNAPSHOT.1 (2026-10-27 21:52)";

    const phone = "221 663 306";

    const email = "isbn@nkp.cz";

    const cookieMessage = navigator.cookieEnabled ? (
      ""
    ) : (
      <Row center="xs">
        <Col xs={6}>
          <Headline size="large" tag="h3">
            <FormattedMessage id="stripes-core.title.cookieEnabled" />
          </Headline>
        </Col>
      </Row>
    );

    return (
      <Form
        onSubmit={onSubmit}
        subscription={{ values: true }}
        render={({
          form,
          submitting,
          handleSubmit,
          submitSucceeded,
          values,
        }) => {
          const { username } = values;
          const submissionStatus = submitting || submitSucceeded;
          const buttonDisabled =
            submissionStatus || !username || !navigator.cookieEnabled;
          const buttonLabel = submissionStatus ? "loggingIn" : "login";
          return (
            <main>
              <div
                className={styles.wrapper}
                style={branding?.style?.login ?? {}}
              >
                <div className={styles.container}>
                  <Row center="xs">
                    <Col xs={6}>
                      <OrganizationLogo />
                    </Col>
                  </Row>
                  <Row>
                    <form
                      className={styles.form}
                      onSubmit={(data) =>
                        handleSubmit(data).then(() =>
                          form.change("password", undefined),
                        )
                      }
                    >
                      <Row center="xs">
                        <Col xs={6}>
                          <div className={styles.formGroup}>
                            {ssoActive && (
                              <SSOLogin handleSSOLogin={handleSSOLogin} />
                            )}
                          </div>
                        </Col>
                      </Row>
                      <Row center="xs">
                        <Col xs={6}>
                          <Headline
                            size="x-large"
                            tag="h1"
                            data-test-h1
                            className={styles.marginBottom0}
                          >
                            <FormattedMessage id="stripes-core.title.loginToRecek" />
                          </Headline>
                        </Col>
                      </Row>
                      {cookieMessage}
                      <div data-test-new-username-field>
                        <RecekField
                          id="input-username"
                          name="username"
                          type="text"
                          component={TextField}
                          inputClass={styles.loginInput}
                          autoComplete="username"
                          autoCapitalize="none"
                          validationEnabled={false}
                          hasClearIcon={false}
                          marginBottom0
                          fullWidth
                          autoFocus
                          labelId="stripes-core.username"
                        />
                      </div>
                      <div data-test-new-username-field>
                        <RecekField
                          id="input-password"
                          component={TextField}
                          name="password"
                          type={showPassword ? "text" : "password"}
                          value=""
                          marginBottom0
                          fullWidth
                          inputClass={styles.loginInput}
                          validationEnabled={false}
                          hasClearIcon={false}
                          autoComplete="current-password"
                          required
                          labelId="stripes-core.password"
                        >
                          <Button
                            type="button"
                            onClick={this.togglePasswordVisibility}
                            marginBottom0
                            buttonStyle="link"
                          >
                            {showPassword ? (
                              <FormattedMessage id="stripes-core.button.hidePassword" />
                            ) : (
                              <FormattedMessage id="stripes-core.button.showPassword" />
                            )}
                          </Button>
                        </RecekField>
                      </div>
                      <Row center="xs">
                        <Col xs={6}>
                          <div className={styles.formGroup}>
                            <Button
                              buttonStyle="primary"
                              id="clickable-login"
                              type="submit"
                              buttonClass={styles.loginSubmitButton}
                              disabled={buttonDisabled}
                              fullWidth
                              marginBottom0
                            >
                              <FormattedMessage
                                id={`stripes-core.${buttonLabel}`}
                              />
                            </Button>
                          </div>
                        </Col>
                      </Row>
                      <Row className={styles.linksWrapper} center="xs">
                        <Col xs={6}>
                          <Row between="xs">
                            <Col
                              xs={12}
                              sm={6}
                              md={4}
                              data-test-new-forgot-password-link
                            >
                              <Button
                                to="/forgot-password"
                                buttonClass={styles.link}
                                type="button"
                                buttonStyle="link"
                              >
                                <FormattedMessage id="stripes-core.button.forgotPassword" />
                              </Button>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      <Row center="xs">
                        <Col xs={6}>
                          <div className={styles.formGroup}>
                            <Button
                              to="/my-publisher/registration"
                              type="button"
                              buttonClass={styles.loginSubmitButton}
                              fullWidth
                              marginBottom0
                            >
                              <FormattedMessage id="stripes-core.register" />
                            </Button>
                          </div>
                        </Col>
                      </Row>
                      <Row center="xs" className={styles.bottomInfo}>
                        <Col xs={6} md={5}>
                          <FormattedMessage
                            id="stripes-core.about.sos"
                            values={{ phone, email }}
                          />
                        </Col>
                        <Col xs={6} md={3}>
                          <FormattedMessage
                            id="stripes-core.about.version"
                            values={{ version }}
                          />
                        </Col>
                      </Row>
                      <Row center="xs">
                        <Col xs={6}>
                          <div className={styles.authErrorsWrapper}>
                            <AuthErrorsContainer errors={authErrors} />
                          </div>
                        </Col>
                      </Row>
                    </form>
                    {ssoActive && <form id="ssoForm" />}
                  </Row>
                </div>
              </div>
            </main>
          );
        }}
      />
    );
  }
}

export default Login;
